import React from "react";
import { PlayerMatchStats, SideFilter, Team } from "../../types/match.types";

interface HeadToHeadViewProps {
	players: PlayerMatchStats[];
	sideFilter: SideFilter;
	team1: Team;
	team2: Team;
	team1Logo: string;
	team2Logo: string;
}

const HeadToHeadView: React.FC<HeadToHeadViewProps> = ({
	players,
	sideFilter,
	team1,
	team2,
	team1Logo,
	team2Logo,
}) => {
	// Separate players by team
	const team1Players = players.filter(p => p.team?.teamId === team1.teamId);
	const team2Players = players.filter(p => p.team?.teamId === team2.teamId);

	if (team1Players.length === 0 || team2Players.length === 0) {
		return (
			<div className="h2h-no-data">
				<p>
					No head-to-head duels found. Players did not kill each other in this
					match.
				</p>
			</div>
		);
	}

	// Helper to get kills between two players
	const getKills = (killer: PlayerMatchStats, victim: PlayerMatchStats): number => {
		if (!killer.killsAgainstPlayers) return 0;
		return killer.killsAgainstPlayers[victim.steamId] || 0;
	};

	return (
		<div className="h2h-matrix-wrapper">
			<div className="h2h-matrix">
				{/* Top-left corner: Team logos */}
				<div className="h2h-corner">
					<div className="corner-teams">
						<div className="corner-team team1-corner">
							<img src={team1Logo} alt={team1.teamName} />
						</div>
						<div className="corner-divider">VS</div>
						<div className="corner-team team2-corner">
							<img src={team2Logo} alt={team2.teamName} />
						</div>
					</div>
				</div>

				{/* Top row: Team 2 players (horizontal) */}
				{team2Players.map((player) => (
					<div key={`top-${player.steamId}`} className="h2h-top-player">
						<div className="player-name">{player.account?.playerName || "Unknown"}</div>
					</div>
				))}

				{/* Rows: Each team1 player vs all team2 players */}
				{team1Players.map((team1Player) => (
					<React.Fragment key={`row-${team1Player.steamId}`}>
						{/* Left column: Team 1 player */}
						<div className="h2h-left-player">
							<div className="player-name">{team1Player.account?.playerName || "Unknown"}</div>
						</div>

						{/* Matrix cells: Kill counts */}
						{team2Players.map((team2Player) => {
							const team1Kills = getKills(team1Player, team2Player);
							const team2Kills = getKills(team2Player, team1Player);
							const team1Wins = team1Kills > team2Kills;
							const team2Wins = team2Kills > team1Kills;
							const tied = team1Kills === team2Kills;

							return (
								<div
									key={`${team1Player.steamId}-${team2Player.steamId}`}
									className={`h2h-cell ${
										team1Wins && !tied ? 'team1-win' : team2Wins && !tied ? 'team2-win' : 'tied'
									}`}
								>
									<span className="kills-left">{team1Kills}</span>
									<span className="kills-divider">×</span>
									<span className="kills-right">{team2Kills}</span>
								</div>
							);
						})}
					</React.Fragment>
				))}
			</div>
		</div>
	);
};

export default HeadToHeadView;